/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-01-06 11:55:26                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-06 16:07:20                               *
 *****************************************************************************/

/* SUMMARY
  * Components
  * Services
  * Validator
  * CompInfosForm: Generic compinfo form
    * handleChange: handle fields change
*/

/* Components */
/***/

/* Services */
import { t } from "i18next";
import { validator } from "../../../services/form-validator/form-validator";
import { Typography, IconButton } from "@material-tailwind/react";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
import { usageFromMoments, usageMoments } from "../compinfos/compinfo-usage";
import { api } from "../../../services/api/api";
import { useEffect, useState } from "react";
import Switch from "../../switch/Switch";
import CompInfoTypeSelect from "./CompInfoTypeSelect";
import TranslationEdit from "../translation-edit/TranslationEdit";
/***/
import "./CompInfoForm.scss";
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/solid";

/* Validator */
export const CompInfoValidator = {
  inputs: (e: EasyTech.InputCompinfo[]) => {
    if (validator.isNotNull(e)) {
      for (let i = 0; i < e.length; i++) {
        if (!validator.isValidCompInfo(e[i].compInfo, e[i].value)) return false;
      }
    } else return false;
    return true;
  },
  selects: (e: EasyTech.SelectCompInfoValue[]) => {
    if (validator.isNotNull(e)) {
      for (let i = 0; i < e.length; i++) {
        if (!validator.isValidSelectCompInfo(e[i].compInfo, e[i].values.map((el) => el.value))) return false;
      }
    } else return false;

    return true;
  }
};
/***/

const USAGE_OPTIONS = ["CREATE", "DURING", "AFTER"] as const;

/** Un choix à saisir : SELECT et RADIO n'en retiennent qu'un, CHECKBOX et MULTIPLESELECT plusieurs. */
const CHOICE_TYPES = ["SELECT", "MULTIPLESELECT", "CHECKBOX", "RADIO"];

/**
 * Same multi-select as the other checklists: react-select with a checkbox on each option.
 * Create + During is CREATE_DURING and does not include After. The three moments together are ALL.
 */
function UsageSelect({
  usage,
  onChange
}: {
  usage?: string,
  onChange: (usage: string) => void
}) {
  const moments = usageMoments(usage);
  const options = USAGE_OPTIONS.map((moment) => ({ value: moment, label: t(moment) }));

  return (
    <MultiCheckSelect
      options={options}
      value={options.filter((option) => moments.includes(option.value))}
      isClearable={false}
      className="w-full"
      placeholder={t("common:select")}
      theme={reactSelectTheme}
      onChange={(selected: { value: string }[] | null) => {
        const next = usageFromMoments((selected || []).map((option: { value: string }) => option.value));
        if (next) onChange(next);
      }} />
  );
}

/**
* CompInfosForm: Generic compinfo form
* @param data - previous data
* @param submitted - subimitted form state
* @param onChange - on field change event
* @param onSumit - on form submit event
*/
export default function CompInfoForm({
  data,
  submitted,
  onChange,
  onSubmit
}: {
  data?: EasyTech.CompInfo,
  submitted?: boolean,
  onChange: (data: EasyTech.CompInfo)  => void, // todo
  onSubmit: () => void // todo
}) {
  const [dataState, setDataState] = useState<EasyTech.CompInfo>(data || {} as EasyTech.CompInfo);
  const [select, setSelectState] = useState<boolean>(true);
  const [languages, setLanguagesState] = useState<EasyTech.Language[]>([]);


  const handleTypeChange = (v: string) => {
    const sel = CHOICE_TYPES.includes(v);
    if (sel)
      setDataState({...dataState, choices: data?.choices ? data?.choices : [{label: "New", enabled: true, id: "", translations: []}], type: v});
    else
      setDataState({...dataState, choices: data?.choices, type: v});
  };

  const handleTranslate = (translations: any) => {
    const tmp = structuredClone(dataState);
    tmp.translations = translations;
    setDataState(tmp);
  };

  const handleChoiceLabel = (choiceIndex: number, label: string) => {
    const tmp = structuredClone(dataState);
    if (!tmp.choices)
      tmp.choices = [];
    tmp.choices[choiceIndex].label = label;
    setDataState(tmp);
  };

  const handleChoiceTranslate = (choiceIndex: number, translations: any) => {
    const tmp = structuredClone(dataState);
    if (!tmp.choices) return;
    translations.forEach((e: any) => {
      if (!tmp.choices) return;
    });
    tmp.choices[choiceIndex].translations = translations;
    setDataState(tmp);
  };

  const handleRemoveChoice = (i: number) => {
    const tmp = structuredClone(dataState);
    if (!tmp.choices) return;
    tmp.choices.splice(i, 1);
    setDataState(tmp);
  };

  const handleAddChoice = () => {
    const tmp = structuredClone(dataState);
    if (!tmp.choices) tmp.choices = [];
    tmp.choices.push({
      label: "",
      enabled: true,
      translations: []
    });
    setDataState(tmp);
  };


  /**
   * useEffect: get compinfos list
   */
  useEffect(() => {
    const sel = CHOICE_TYPES.includes(data && data.type ? data.type : "");
    setSelectState(sel);
  }, [data]);
  /***/

  /**
  * useEffect: get compinfos list
  */
  useEffect(() => {
    api.get("users/languages")
      .then(ret  => {
        setLanguagesState(ret as EasyTech.Language[]);        
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);
  /***/


  /**
  * useEffect: get compinfos list
  */
  useEffect(() => {
    onChange(dataState);
  }, [dataState, onChange]);
  /***/

  if (!data) return;
  return (
    <>
      <div className="comp-field flex justify-between">
        <Typography>{t("common:entity")}</Typography>
        <div className="form-value">
          <Typography>{data.service}</Typography>
        </div>
      </div>

      <div className="comp-field flex justify-between">
        <Typography>{t("common:usage")}</Typography>
        <div className="form-value">
          <UsageSelect
            usage={dataState.usage}
            onChange={(usage) => setDataState({ ...dataState, usage })} />
        </div>
      </div>
      
      <div className="comp-field">
        <Typography>{t("common:label")}</Typography>
        <input type="text"
                value={data?.label || ""}
                onChange={(e) => setDataState({...dataState, label: e.target.value})}
                className={`${(submitted && (!data || !data?.label)) && "error"}`}
                placeholder={t("common:label")}/>
      </div>
      <div className="comp-field flex justify-between">
        <Typography>{t("common:labelTranslation")}</Typography>

        <div>
          <div className="form-value">
            <TranslationEdit data={dataState.translations} onChange={(v) => handleTranslate(v)} languages={languages}/>
          </div>
        </div>
      </div>

      <div className="comp-field justify-between">
        <Typography>{t("common:type")}</Typography>
        <div className="form-value">
          <CompInfoTypeSelect value={dataState.type} onChange={(v) => handleTypeChange(v) } />
        </div>
      </div>

      {select &&
        <div className="comp-field justify-between">
          <Typography>{t("common:values")}</Typography>
          <div className="form-value">
            <div>
              {dataState.choices && dataState.choices.map((c: EasyTech.CompInfoChoice, i: number) => (
                <div className="my-3">

                    <div className="flex items-center justify-around">
                    <Typography>{t("common:valueIndex", { index: i })}</Typography>

                    <input type="text"
                        value={c.label || ""}
                        onChange={(v) => handleChoiceLabel(i, v.target.value)}
                        className={`w-[55%] ${(submitted && (!data || !data?.label)) && "error"}`}
                        placeholder={t("common:label")}/>
                    </div>

                    <div className="flex items-center">
                    
                      <TranslationEdit key={c.id} data={c.translations} onChange={(v) => handleChoiceTranslate(i, v)} languages={languages}/>
                      <IconButton variant="text" size="sm" color="blue" onClick={() => handleRemoveChoice(i)}>
                        <MinusCircleIcon className="w-6" />
                      </IconButton>
                    </div>
                </div>
              ))}

              <IconButton variant="text" size="sm" color="blue" onClick={() => handleAddChoice()}>
                <PlusCircleIcon className="w-6" />
              </IconButton>
            </div>
          </div>
        </div>
      }

      <div className="comp-field flex justify-between">
        <Typography>{t("common:required")}</Typography>
        <div className="form-value">
          <Switch checked={dataState.constraints === "REQUIRED"}
            onChange={() => setDataState({...dataState, constraints: dataState.constraints === "REQUIRED" ? "OPTIONAL" : "REQUIRED"})}/>
        </div>
      </div>

      <div className="comp-field flex justify-between">
        <Typography>{t("common:showInInvoice")}</Typography>
        <div className="form-value">
          <Switch checked={dataState.sendToInvoice ?? false}
              onChange={(e) => setDataState({...dataState, sendToInvoice: e})}/>
        </div>
      </div>

      <div className="comp-field flex justify-between">
        <Typography>{t("common:enable")}</Typography>
        <div className="form-value">
          <Switch checked={data?.enabled}
              onChange={(e) => setDataState({...dataState, enabled: e})}/>
        </div>
      </div>
    </>
  );
}
/***/
